import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import CalendarSelector from "./CalendarSelector";
import { DateTime } from "luxon";
import ThemedAddToCartButton from "./ThemedAddToCartButton";
import ThemedCheckoutButton from "./ThemedCheckoutButton";
import ThemedText from "./ThemedText";
import ThemedBgElement from "./ThemedBgElement";
import TicketListWrapper from "./TicketListWrapper";

const TicketBookingPage = ({ ticketType }) => {
//  console.log({ticketType, version: 2})
  const [selectedTimeslot, setSelectedTimeslot] = useState(null);
  const [selectedTimeslots, setSelectedTimeslots] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketQuantities, setTicketQuantities] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(1); // 1: Select date & time, 2: Select tickets
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selectedTimeslotsByDate, setSelectedTimeslotsByDate] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [hasItemsInCart, setHasItemsInCart] = useState(
    ticketType.cartItemCount > 0,
  );

  const theme = ticketType.currentTheme;

  const themeTypographyClasses = {
    pink: {
      title: "font-poppins font-thin text-[45px] leading-[1] tracking-[0.05em] text-white",
      shortDescription: "font-['Poppins'] font-bold text-[22px] leading-[20px] tracking-[0.05em] text-white",
      description: "font-['Poppins'] text-white prose-strong:text-white",
      bulletedDetails: "font-['Poppins'] font-bold text-[20px] leading-[30px] tracking-[0.05em]",
      heading: "font-['Poppins'] font-medium text-[22px] leading-[20px] tracking-[0.05em]",
      subheading: "font-poppins font-thin text-[10px] leading-[16px] tracking-[0.05em]",
      textTitle: 'text-pink-primary-500',
      paragraphe: 'text-pink-primary-500',
    },
    blue: {
      heading: 'font-montserrat text-white text-[90px] leading-[40px] tracking-[0] uppercase',
      subheading: 'font-montserrat font-black leading-[40px] tracking-[0]',
      title: 'font-montserrat text-black text-7xl tracking-[0] uppercase text-white font-bold',
      shortDescription: "font-montserrat text-blue-primary-500 text-[29px] font-extrabold leading-[40px] tracking-[0] uppercase",
      textTitle: 'text-gray-900',
      paragraphe: 'text-gray-900',
      description: 'text-white prose-headings:text-white prose-p:text-white prose-strong:text-white prose-em:text-white prose-li:text-white prose-a:text-white'
    },
    default: {
      heading: 'text-black',
      textTitle: 'text-gray-900',
      paragraphe: '',
    }
  };


  const bookingFlowRef = useRef(null);
  const addToCartUrl = ticketType.add_to_cart_url;
  let subHeadingColor = 'text-white'

  if (ticketType.currentTheme === 'blue') {
    subHeadingColor = 'text-blue-primary-500'
  }

  // Track scroll position for sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (bookingFlowRef.current) {
        setScrollPosition(bookingFlowRef.current.scrollTop);
      }
    };

    const bookingFlowElement = bookingFlowRef.current;
    if (bookingFlowElement) {
      bookingFlowElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (bookingFlowElement) {
        bookingFlowElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Calculate the total ticket count and total price
  const orderSummary = useMemo(() => {
    let totalTickets = 0;

    // Sum up all the quantities
    Object.values(ticketQuantities).forEach((qty) => {
      totalTickets += qty;
    });

    return { totalTickets };
  }, [ticketQuantities]);

  // Set active step based on scroll position or user interaction
  const goToStep = (step) => {
    setActiveStep(step);

    // Scroll to the corresponding section
    const sectionMap = {
      1: "date-time-selection",
      2: "ticket-selection",
    };

    const sectionId = sectionMap[step];
    const section = document.getElementById(sectionId);
    if (section && bookingFlowRef.current) {
      bookingFlowRef.current.scrollTo({
        top: section.offsetTop - 60, // Account for sticky header
        behavior: "smooth",
      });
    }
  };

  // Update ticket quantities
  const updateQuantity = (ticketId, change) => {
    setTicketQuantities((prev) => {
      const currentValue = prev[ticketId] || 0;
      const availableQty =
        tickets.find((t) => t.id === ticketId)?.available_quantity || 0;
      let newValue = currentValue + change;
      if (newValue <= 0) {
        return { ...prev, [ticketId]: 0 };
      }
      // For scheduled tickets, cap at ride capacity; for non-scheduled, cap at available inventory
      if (ticketType.is_scheduled) {
        const maxCap = ticketType.maxQuantity * ticketType.rideCount;
        if (maxCap > 0 && newValue >= maxCap) {
          newValue = maxCap;
        }
      } else {
        if (availableQty > 0 && newValue > availableQty) {
          newValue = availableQty;
        }
      }
      return { ...prev, [ticketId]: newValue };
    });
  };

  // called by each card to remove himself from the card list.
  const removeTicket = useCallback(
    (ticketId) => {
      const remainingTickets = tickets.filter(
        (ticket) => ticket.id !== ticketId,
      );

      setTickets(remainingTickets);
      // Get the timeslots that still have tickets in the remaining list
      const remainingTimeslotIds = [
        ...new Set(
          remainingTickets
            .filter((ticket) => ticket.timeslot)
            .map((ticket) => ticket.timeslot.id),
        ),
      ];

      setSelectedTimeslots((prev) =>
        prev.filter((timeslot) => remainingTimeslotIds.includes(timeslot.id)),
      );

      // Also clear quantity for removed ticket
      setTicketQuantities((prev) => {
        const newQuantities = { ...prev };
        delete newQuantities[ticketId];
        return newQuantities;
      });
    },
    [tickets],
  );

  const fetchTickets = useCallback(
    async (timeslotsToFetch) => {
      setIsLoading(true);
      try {
        const fetchPromises = timeslotsToFetch.map(async (timeslot) => {
          const url = `/api/fetch-tickets/${ticketType.is_scheduled
            ? `?timeslot_id=${timeslot.id}`
            : `?ticket_type_id=${ticketType.id}`
            }`;

          const response = await fetch(url);
          if (!response.ok)
            throw new Error(
              `Failed to fetch tickets for timeslot ${timeslot.id}`,
            );

          const data = await response.json();
          return data.tickets || [];
        });

        const newTicketsArrays = await Promise.all(fetchPromises);
        const newTickets = newTicketsArrays.flat();

        setTickets((prevTickets) => [...prevTickets, ...newTickets]);
        setTicketQuantities({});
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [ticketType.id, ticketType.is_scheduled],
  );

  // Handle timeslot selection
  const handleTimeslotSelect = (slot) => {
    setSelectedTimeslot(slot);

    // Store selected timeslots by date for easy display in summary
    setSelectedTimeslotsByDate((prev) => {
      const date = slot.selectedDate;
      const timeslotsForDate = [...(prev[date] || [])];

      // Check if slot already exists
      const existingIndex = timeslotsForDate.findIndex(
        (ts) => ts.id === slot.id,
      );

      if (existingIndex >= 0) {
        // Remove if already selected
        timeslotsForDate.splice(existingIndex, 1);
      } else {
        // Add if not selected
        timeslotsForDate.push(slot);
      }

      // If array is empty, remove the date key
      if (timeslotsForDate.length === 0) {
        const { [date]: _, ...rest } = prev;
        return rest;
      }

      return { ...prev, [date]: timeslotsForDate };
    });
  };

  // Handle adding a timeslot
  const handleAddTimeSlot = (slot, selectedDate) => {
    slot.selectedDate = selectedDate;
    setSelectedTimeslots((prev) => {
      if (prev.find((el) => el.id === slot.id) !== undefined) {
        // Remove if already selected
        return prev.filter((el) => el.id !== slot.id);
      }
      // Add if not selected
      return [...prev, slot];
    });
    handleTimeslotSelect(slot);
  };

  // Fetch tickets when timeslot is selected or on initial load for non-scheduled tickets
  useEffect(() => {
    // For non-scheduled tickets, fetch immediately on component mount
    if (!ticketType.is_scheduled && ticketType.id) {
      fetchTickets([{ id: null }]); // Pass dummy timeslot for non-scheduled
      return;
    }

    // Only run effect if we have valid inputs for scheduled tickets
    if (
      ticketType.is_scheduled &&
      (!selectedTimeslots || selectedTimeslots.length === 0)
    ) {
      return;
    }

    // 1. Sync tickets with selected timeslots (remove unselected)
    const selectedTimeslotIds = selectedTimeslots.map((ts) => ts.id);

    setTickets((prevTickets) => {
      const filteredTickets = prevTickets.filter(
        (ticket) =>
          !ticket.timeslot || selectedTimeslotIds.includes(ticket.timeslot.id),
      );

      // 2. Determine which timeslots need fetching
      const timeslotsToFetch = selectedTimeslots.filter(
        (timeslot) =>
          !filteredTickets.some(
            (ticket) => ticket.timeslot?.id === timeslot.id,
          ),
      );

      // 3. Fetch new tickets if needed
      if (timeslotsToFetch.length > 0) {
        // Schedule after this render completes
        setTimeout(() => fetchTickets(timeslotsToFetch), 0);
      }

      return filteredTickets;
    });
  }, [selectedTimeslots, ticketType.is_scheduled, ticketType.id, fetchTickets]);

  // Show notification message
  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "success" });
    }, 3000);
  };

  // Add to cart via API call
  const addToCart = async () => {
    // Validate selection
    const selectedTicketsEntries = Object.entries(ticketQuantities).filter(
      ([_, quantity]) => quantity > 0,
    );

    if (selectedTicketsEntries.length === 0) {
      alert("Please select at least one ticket");
      return;
    }

    if (ticketType.is_scheduled && selectedTimeslots.length === 0) {
      alert("Please select at least one time slot");
      return;
    }

    setIsLoading(true);

    try {
      // Prepare the data structure
      const timeslotSelections = [];
      // Process each selected ticket with quantity > 0
      selectedTicketsEntries.forEach(([ticketId, quantity]) => {
        // For scheduled tickets
        if (ticketType.is_scheduled) {
          const ticket = tickets.find((t) => t.id === parseInt(ticketId));
          if (!ticket) {
            console.error(
              `Ticket ID ${ticketId} not found in available tickets`,
            );
            return;
          }

          timeslotSelections.push({
            ticket_id: parseInt(ticketId),
            timeslot_id: ticket.timeslot?.id,
            quantity,
          });
        }
        // For non-scheduled tickets
        else {
          // Format as "non-scheduled-{ticket_type_id}" as expected by backend
          const nonScheduledTicketId = `${ticketType.id}`;

          timeslotSelections.push({
            ticket_id: parseInt(ticketId),
            quantity,
          });
        }
      });

      if (timeslotSelections.length === 0) {
        throw new Error("No valid tickets could be processed");
      }

      // Get the CSRF token from the DOM
      const csrfToken = document.querySelector(
        'input[name="csrfmiddlewaretoken"]',
      )?.value;
      if (!csrfToken) {
        console.warn(
          "CSRF token not found. Adding hidden form to obtain token.",
        );
        // Create a hidden input to get the token if not already available
        const tokenElement = document.createElement("input");
        tokenElement.type = "hidden";
        tokenElement.name = "csrfmiddlewaretoken";
        document.body.appendChild(tokenElement);
      }

      // Get the token (either existing or newly created)
      const token =
        document.querySelector('input[name="csrfmiddlewaretoken"]')?.value ||
        "";

      // Send the request
      const response = await fetch(addToCartUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": token,
        },
        body: JSON.stringify({ timeslot_selections: timeslotSelections }),
      });

      let responseData;
      try {
        // Try to parse as JSON first
        responseData = await response.json();
      } catch (e) {
        // If JSON parsing fails, get the text
        const text = await response.text();
        console.error("Response was not JSON:", text);
        responseData = { error: "Invalid response format" };
      }

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to add items to cart");
      }

      // Show success message
      showNotification("Items added to cart successfully!");

      // Track GA4 add_to_cart event
      if (window.ga4Ecommerce && window.ga4Ecommerce.canSendEvents()) {
        try {
          // Prepare cart data for GA4
          const cartItems = selectedTicketsEntries.map(
            ([ticketId, quantity]) => {
              const ticket = tickets.find((t) => t.id === parseInt(ticketId));
              return {
                item_id: ticketId,
                item_name: ticket?.name || ticketType.name,
                item_category: "Ticket",
                quantity: quantity,
                price: ticket?.price || 0,
                location_id: ticket?.location_id || "",
                timeslot_id: ticket?.timeslot?.id || "",
              };
            },
          );

          const totalValue = cartItems.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
          );

          const cartData = {
            currency: "USD",
            value: totalValue,
            items: cartItems,
          };
          window.ga4Ecommerce.trackAddToCart(cartData);
        } catch (trackingError) {
          console.warn("GA4 tracking failed:", trackingError);
        }
      }

      // Update cart status
      setHasItemsInCart(true);

      // Reset quantities to 0 after successful addition
      const resetQuantities = {};
      Object.keys(ticketQuantities).forEach((id) => {
        resetQuantities[id] = 0;
      });
      setTicketQuantities(resetQuantities);
    } catch (error) {
      console.error("Error adding to cart:", error);
      showNotification(
        error.message || "Failed to add items to cart. Please try again.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle booking (redirect to cart page)
  const handleBooking = () => {
    const selectedTickets = Object.entries(ticketQuantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([ticketId, quantity]) => `${ticketId}:${quantity}`);

    if (selectedTickets.length === 0) {
      alert("Please select at least one ticket");
      return;
    }

    const searchParams = new URLSearchParams();
    selectedTickets.forEach((ticket) =>
      searchParams.append("tickets[]", ticket),
    );

    if (ticketType.is_scheduled && !selectedTimeslot) {
      alert("Please select a time slot");
      return;
    }

    if (selectedTimeslot) {
      searchParams.append("timeslot", selectedTimeslot.id);
    }

    window.location.href = `/cart/?${searchParams.toString()}`;
  };

  // Get progress percentage
  const getProgressPercentage = () => {
    switch (activeStep) {
      case 1:
        return 50;
      case 2:
        return 100;
      default:
        return 0;
    }
  };

  // Format date for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "";
    const date = DateTime.fromISO(dateStr, { zone: ticketType.timezone });
    return date.toFormat("ccc, MMMM dd");
  };

  const hasSelectedTickets = Object.values(ticketQuantities).some(
    (qty) => qty > 0,
  );
  const bookingDisabled =
    !hasSelectedTickets || (ticketType.is_scheduled && !selectedTimeslot);

  return (
    <div>
      {/* Notification toast */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${notification.type === "success"
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
            }`}
        >
          <div className="flex items-center">
            {notification.type === "success" ? (
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                ></path>
              </svg>
            ) : (
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 id='title' className={`text-4xl mb-2 ${ticketType.currentTheme === 'default' ? 'text-black' : themeTypographyClasses[ticketType.currentTheme].title} `}>
          {ticketType.name}
        </h1>
        <h3 id='shortDescription' className={`mb-4 ${ticketType.currentTheme === 'default' ? 'text-black' : themeTypographyClasses[ticketType.currentTheme].shortDescription}`}>{ticketType.shortDescription}</h3>
        <div
          id='description'
          className={`prose ${ticketType.currentTheme === 'default' ? 'text-black' : 'text-white prose-strong:text-white'}  ${themeTypographyClasses[theme].description}`}
          dangerouslySetInnerHTML={{ __html: ticketType.description }}
        />
      </div>

      {ticketType.is_scheduled ? (

        <div className="bg-white w-full shadow-lg rounded-lg overflow-hidden">
          {/* Sticky progress tracker */}
          <div
            className={`sticky top-0 z-20 bg-white border-b shadow-sm transition-shadow duration-300 ${scrollPosition > 20 ? "shadow-md" : ""}`}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-medium text-gray-900">Booking Progress</h2>
                {orderSummary.totalTickets > 0 && (
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">
                      {orderSummary.totalTickets}
                    </span>{" "}
                    ticket{orderSummary.totalTickets !== 1 ? "s" : ""} selected
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="w-full bg-gray-200 h-2.5">
                  <ThemedBgElement
                    theme={theme}
                    baseClass="h-2.5 transition-all duration-300"
                    rounded={theme === "pink"}
                    pinkClass="bg-pink-secondary-500"
                    blueClass="bg-blue-primary-500"
                    style={{ width: `${getProgressPercentage()}%` }}
                  />
                </div>
              </div>

              <div className="flex justify-between mt-2 text-sm">
                <button
                  onClick={() => goToStep(1)}
                  className={`flex flex-col items-center ${activeStep >= 1
                    ? theme === 'pink'
                      ? 'text-pink-secondary-500 font-medium'
                      : 'text-blue-primary-500 font-medium'
                    : 'text-gray-400'
                    }`}
                >
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full mb-1 ${activeStep >= 1
                      ? theme === 'pink'
                        ? 'bg-pink-secondary-600 text-white '
                        : 'bg-blue-primary-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    1
                  </span>
                  <ThemedText
                    theme={theme}
                    baseClass=""
                    pinkClass="text-pink-primary-600"
                    blueClass="text-blue-900"
                  >
                    Date & Time
                  </ThemedText>
                </button>

                <button
                  onClick={() => goToStep(2)}
                  className={`flex flex-col items-center ${activeStep >= 2
                    ? theme === 'pink'
                      ? 'text-pink-primary-500 font-medium'
                      : 'text-blue-primary-500 font-medium'
                    : 'text-gray-400'
                    }`}
                  disabled={selectedTimeslots.length === 0}
                >
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full mb-1 ${activeStep >= 2
                      ? theme === 'pink'
                        ? 'bg-pink-secondary-600 text-white'
                        : 'bg-blue-primary-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                      }`}
                  >
                    2
                  </span>
                  <ThemedText
                    theme={theme}
                    baseClass=""
                    pinkClass="text-pink-primary-600"
                    blueClass="text-blue-900"
                  >
                    Tickets
                  </ThemedText>
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable booking flow content */}
          <div
            ref={bookingFlowRef}
            className="overflow-y-auto max-h-[calc(100vh-200px)] p-6"
          >
            {/* Calendar Component - only show for scheduled tickets */}
            <CalendarSelector
              onTimeslotSelect={setSelectedTimeslot}
              selectedTimeslot={selectedTimeslot}
              addTimeSlot={handleAddTimeSlot}
              selectedTimeslots={selectedTimeslots}
              activeStep={activeStep}
              selectedTimeslotsByDate={selectedTimeslotsByDate}
              formatDateForDisplay={formatDateForDisplay}
              goToStep={goToStep}
              theme={theme}
            />

            {/* Section 2: Selected Slots Summary and Ticket Selection */}
            <section
              id="ticket-selection"
              className={`mb-8 ${activeStep === 1 ? "hidden" : ""} ${selectedTimeslots.length === 0 ? "opacity-50 pointer-events-none" : ""}`}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-xl font-medium ${theme === 'pink' ? 'text-pink-primary-600' : 'text-gray-900'} `}>
                  2. Selected Time Slots
                </h2>
                {selectedTimeslots.length > 0 && (
                  <button
                    onClick={() => goToStep(1)}
                    className={`${theme === 'pink' ? 'text-pink-primary-500 hover:text-pink-primary-600' : 'text-blue-600 hover:text-blue-800'}  text-sm font-extralight`}
                  >
                    + Add More Time Slots
                  </button>
                )}
              </div>

              {Object.keys(selectedTimeslotsByDate).length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  {Object.entries(selectedTimeslotsByDate).map(
                    ([date, slots]) => (
                      <div key={date} className="mb-4 last:mb-0">
                        <h3 className="font-medium text-gray-800 mb-2">
                          {formatDateForDisplay(date)}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((slot) => (
                            <div
                              key={slot.id}
                              className="flex items-center bg-white border border-gray-200 rounded-lg px-3 py-2"
                            >
                              <span className="text-sm font-medium text-gray-800">
                                {slot.formatted_time}
                              </span>
                              <button
                                onClick={() =>
                                  handleAddTimeSlot(slot, slot.selectedDate)
                                }
                                className="ml-2 text-gray-400 hover:text-red-500"
                                aria-label="Remove time slot"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No time slots selected yet</p>
                  <button
                    onClick={() => goToStep(1)}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Start by selecting a date and time
                  </button>
                </div>
              )}
            </section>

            {/* Tickets Component */}
            {(!ticketType.is_scheduled ||
              (ticketType.is_scheduled &&
                selectedTimeslots.length > 0 &&
                activeStep === 2)) && (
                <div className="mt-6">
                  <TicketListWrapper
                    tickets={tickets}
                    ticketType={ticketType}
                    quantities={ticketQuantities}
                    updateQuantity={updateQuantity}
                    isLoading={isLoading}
                    vehicleCapacity={ticketType.maxQuantity}
                    vehicleCount={ticketType.rideCount}
                    removeTicket={removeTicket}
                    heading="Select Your Tickets"
                    description="Choose the number of tickets for each time slot below."
                    showDescription={true}
                    wrapperClass="bg-white border border-gray-200 rounded-lg shadow-sm pt-2"
                  />
                </div>
              )}
          </div>

          {/* Sticky footer with summary and action button */}
          {selectedTimeslots.length > 0 && (
            <div className="sticky bottom-0 bg-white border-t shadow-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Selected Slots:</p>
                  <p className={`font-medium ${theme === 'pink' ? "text-pink-primary-600" : 'text-blue-primary-600'}`}>
                    {selectedTimeslots.length} time slot
                    {selectedTimeslots.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <ThemedAddToCartButton
                    theme={theme}
                    onClick={activeStep === 1 ? () => goToStep(2) : addToCart}
                    disabled={(activeStep === 2 && bookingDisabled) || isLoading}
                    isLoading={isLoading}
                    showContinue={activeStep === 1}
                    continueText="Continue to Tickets"
                    addText="Add To Cart"
                  />
                  {hasItemsInCart && (
                    <div className="flex gap-2">
                      <ThemedCheckoutButton
                        theme={theme}
                        text="Checkout"
                      />
                    </div>
                  )}
                </div>


              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Simplified view for non-scheduled tickets */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-4">
              Select Your Tickets
            </h2>
            <TicketListWrapper
              tickets={tickets}
              ticketType={ticketType}
              quantities={ticketQuantities}
              updateQuantity={updateQuantity}
              isLoading={isLoading}
              vehicleCapacity={ticketType.maxQuantity}
              vehicleCount={ticketType.rideCount}
              removeTicket={removeTicket}
              heading=""
              showDescription={false}
              wrapperClass=""
              isScheduled={ticketType.is_scheduled}
            />
          </div>

          {/* Sticky footer for non-scheduled tickets */}
          <div className="sticky bottom-0 bg-white shadow-lg p-4">
            <div className="flex justify-end">
              <div className="flex gap-2">
                <ThemedAddToCartButton
                  theme={theme}
                  onClick={addToCart}
                  disabled={!hasSelectedTickets || isLoading}
                  isLoading={isLoading}
                  showContinue={false}
                  addText="Add To Cart"
                />
                {hasItemsInCart && (
                  <ThemedCheckoutButton
                    theme={theme}
                    text="Proceed to Checkout"
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {hasItemsInCart && (
        <div className="mt-6 bg-white border-2 rounded-lg shadow-lg overflow-hidden">
          <div className="bg-indigo-50 px-6 py-4 border-b border-gray-200">
            <h3 className={`text-lg font-medium  ${themeTypographyClasses[theme].textTitle}`}>
              Discover More Experiences
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Enhance your visit with these additional tickets available on our
              platform
            </p>
          </div>
          <ul className="w-full">
            {ticketType.ticketTypes.map((type) => (
              <li
                key={type.id}
                className={`px-6 py-4 hover:bg-gray-50 transition duration-150 `}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-md font-medium  truncate ${themeTypographyClasses[theme].paragraphe}`}>
                      {type.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Add this experience to your adventure
                    </p>
                  </div>
                  <a
                    href={`/booking/${type.slug}/`}

                  ><button
                    className={`p-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition duration-150 ease-in-out ${theme === 'pink'
                      ? 'text-pink-primary-500 border border-pink-primary-500 hover:bg-pink-primary-50 focus:ring-pink-primary-500'
                      : 'text-blue-primary-500 border border-blue-primary-500 hover:bg-blue-50 focus:ring-blue-primary-500'
                      }`}
                  >
                      Buy Now
                    </button>
                  </a>
                </div>
              </li>
            ))}
          </ul>
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
            <a
              href={`/`}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500 flex items-center justify-center"
            >
              View all available tickets
              <svg
                className="ml-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketBookingPage;